import type { InitConfig } from "./main";
import type { WidgetProps } from "./ws-element/elements/WebComponent";
import type Websail from "./main";
import version from "./version";
import { logBuild } from "../../shared/buildInfo";
import { renderWidgetError, bootstrapWidgetErrorMessages } from "./errors/errorMessages";

// The compatibility loader is an IIFE, so import.meta.url is unavailable.
// Module script embeds also have no document.currentScript.
const loaderAssetUrl = typeof document === "undefined" ? undefined :
  (document.currentScript as HTMLScriptElement | null)?.src ||
  Array.from(document.scripts).find((script) =>
    /\/(bee-form\.min\.js|dev-entry\.ts|index\.ts)(\?|$)/.test(script.src),
  )?.src;
const loaderBuildDetails = {
  assetUrl: loaderAssetUrl,
  loadsBundleHash: import.meta.env.BEE_BUNDLE_VERSION,
};

logBuild("booking-loader", loaderBuildDetails);

const widgetStore = new Map<string, Promise<WidgetProps[]>>();

function loadWidgets(apiBaseUrl: string) {
  const url = new URL("/api/widgets", apiBaseUrl);

  if (!widgetStore.get(url.href)) {
    widgetStore.set(
      url.href,
      fetch(url.href, {
        headers: { Accept: "application/json" },
        signal: AbortSignal.timeout(15000),
      })
        .then(async res => {
          if (!res.ok) throw new Error("Widget configuration request failed");
          const result = await res.json() as { body?: WidgetProps[] };
          if (!Array.isArray(result.body)) throw new Error("Invalid widget configuration response");
          return result.body;
        })
        .catch(error => {
          widgetStore.delete(url.href);
          throw error;
        }),
    );
  }

  return widgetStore.get(url.href)!;
}

export class WebsailLoader {
  get VERSION() {
    return version.VERSION;
  }

  get COMMIT_HASH() {
    return version.COMMIT_HASH;
  }

  get LAST_COMMIT_DATE() {
    return version.LAST_COMMIT_DATE_TIME;
  }

  args = [] as Array<InitConfig>;
  constructorArgs = [] as Array<InitConfig>;
  isAdmin = false;
  private bundleRetry = 0;

  static async getIntegration(integrationName: string) {
    let integration =
      window.wsBundleIntegration.getIntegration(integrationName);

    if (integration?.apiBaseUrl) return integration;

    return new Promise<Websail>((resolve, reject) => {
      const timeout = setTimeout(() => {
        window.removeEventListener("ws-integration-init", onInit);
        reject(new Error("Widget integration did not initialize"));
      }, 15000);
      const onInit = () => {
        integration =
          window.wsBundleIntegration.getIntegration(integrationName);

        if (integration?.apiBaseUrl) {
          clearTimeout(timeout);
          window.removeEventListener("ws-integration-init", onInit);
          resolve(integration);
        }
      };
      window.addEventListener("ws-integration-init", onInit);
    });
  }

  static async fetchWidgets(integrationName: string, refresh = false) {
    const integration = await this.getIntegration(integrationName);
    const apiBaseURL = integration?.apiBaseUrl;

    if (!apiBaseURL) {
      return [];
    }

    if (refresh) widgetStore.delete(new URL("/api/widgets", apiBaseURL).href);

    return loadWidgets(apiBaseURL) ?? [];
  }

  onLoadBundle = () => {
    for (const constructorArgs of this.constructorArgs) {
      const integration = new window.wsBundleIntegration(constructorArgs);
      integration.init(this.args.shift());
    }

    this.init(this.args.shift());
  };

  loadScripts = (config?: InitConfig) => {
    if (!config?.apiBaseUrl) {
      return;
    }

    if (window.websailBundle) {
      return;
    }

    let basePath = config.apiBaseUrl;
    let pathname = "/bee-react-form/";

    if (config.publicUrl) {
      basePath = URL.parse(config.publicUrl)?.href || basePath;
      pathname = URL.parse(config.publicUrl)?.pathname || "";
      if (!pathname.endsWith("/")) {
        pathname += "/";
      }
    }

    // Prefetch only. The widget's bootstrap owns its visible error and retry.
    void loadWidgets(config.apiBaseUrl).catch(() => undefined);

    if (basePath.indexOf("?") >= 0) {
      basePath = basePath.slice(0, basePath.indexOf("?"));
    }

    let filePath = new URL(`${pathname}bundle.min.js`, basePath).href;
    filePath += "?v=" + (import.meta.env.BEE_BUNDLE_VERSION || this.VERSION);
    // Browsers cache failed module loads for the lifetime of the document.
    if (this.bundleRetry) filePath += "&retry=" + this.bundleRetry;

    if (import.meta.env.MODE === "development") {
      return import("./main").then(() => {
        this.onLoadBundle();
      });
    }

    if (document.getElementById("ws-integration-bundle-script")) {
      return;
    }

    const sNew = document.createElement("script");
    sNew.id = "ws-integration-bundle-script";
    sNew.onload = this.onLoadBundle;
    sNew.onerror = () => {
      sNew.remove();
      this.bundleRetry += 1;
      const copy = bootstrapWidgetErrorMessages;
      document.querySelectorAll<HTMLElement>(".ws-element, ws-widget").forEach(element => {
        renderWidgetError(element, { message: copy.load, retryLabel: copy.retry, onRetry: () => {
          element.replaceChildren();
          this.loadScripts(config);
        } });
      });
    };
    sNew.async = true;
    sNew.type = "module";
    sNew.crossOrigin = "anonymous";
    sNew.src = filePath;

    document.body.append(sNew);
  };

  constructor(config?: Partial<InitConfig> & { loader?: boolean }) {
    if (config?.loader !== true && config?.apiBaseUrl) {
      void loadWidgets(config.apiBaseUrl).catch(() => undefined);

      if (config) this.constructorArgs.push(config as InitConfig);

      if (window.wsBundleIntegration) {
        this.onLoadBundle();
        return;
      }
    }

    const websailConfig = window.websailConfig;

    if (websailConfig && "apiBaseUrl" in websailConfig) {
      this.args.push(websailConfig);
    }

    this.loadScripts({ ...config, ...websailConfig } as InitConfig);
  }

  log() {
    logBuild("booking-loader", { ...loaderBuildDetails, force: true });
  }

  init = (config?: InitConfig) => {
    if (!config) {
      return;
    }

    this.loadScripts(config);

    const wsBundleIntegration = window.wsBundleIntegration;
    if (wsBundleIntegration) {
      this.args.unshift(config);
      new wsBundleIntegration(this.args.shift()).init();
      return;
    }

    this.args.push(config);
  };

  initAdmin() {
    this.isAdmin = true;
  }

  onRedirect: undefined | ((url: string) => boolean | void);
}

declare global {
  interface Window {
    websail: WebsailLoader;
    wsintegration: typeof WebsailLoader;
    websailConfig?: InitConfig;
  }
}
